/**
 * Scenario presets — historical situations, each fought on its real-world theatre (theatres/)
 * where one exists, else on a generated map. Each preset is purely structural (theatre or size,
 * season, asymmetries); names and briefings are localised by the client (keyed by `id`). Slot i
 * plays the theatre's nation i; slot 0 is always the player, the rest are AI unless a host
 * overrides them.
 *
 * Every scenario is a normal `SessionConfig`, so replays, multiplayer and the AI work unchanged.
 */
import type { AiDifficulty, FactionSetup, MapSize, Season, SessionConfig, TechId } from './core/types';
import { THEATRES, type TheatreId } from './theatres/registry';

export type ScenarioId = 'tannenberg' | 'rasputitsa' | 'ghost-army' | 'enigma' | 'winter-kessel';

export interface ScenarioDef {
  id: ScenarioId;
  /** Real episode the situation is modelled on (for the briefing). */
  inspiredBy: string;
  /** Real-world theatre the scenario is fought on (its map, nations and board size). */
  theatre?: TheatreId;
  mapSize: MapSize;
  season: Season;
  dayLimit: number;
  vpVictoryShare: number;
  /** Slot 0 = player. Controllers here are defaults; hosts may override. */
  factions: FactionSetup[];
  startingTechs?: TechId[];
}

const ai = (aiDifficulty: AiDifficulty, extra: Omit<FactionSetup, 'controller'> = {}): FactionSetup => ({
  controller: 'ai',
  aiDifficulty,
  ...extra,
});

export const SCENARIOS: Record<ScenarioId, ScenarioDef> = {
  /** 1914: the Russian armies radioed their orders in the clear; the Germans listened and encircled. */
  tannenberg: {
    id: 'tannenberg',
    inspiredBy: 'Tannenberg, August 1914',
    theatre: 'east-prussia-1914',
    mapSize: THEATRES['east-prussia-1914'].mapSize,
    season: 'summer',
    dayLimit: 20,
    vpVictoryShare: 0.65,
    factions: [
      { controller: 'human', army: 1.1, techs: ['codebreaking', 'field_telephone', 'railways'] },
      ai('veteran', { techs: ['radio'], army: 1.3 }),
    ],
  },
  /** 1941: the autumn mud stopped the armoured drive on Moscow. Hold until the ground turns. */
  rasputitsa: {
    id: 'rasputitsa',
    inspiredBy: 'Operation Typhoon, October 1941',
    theatre: 'moscow-1941',
    mapSize: THEATRES['moscow-1941'].mapSize,
    season: 'autumn',
    dayLimit: 30,
    vpVictoryShare: 0.8,
    factions: [
      { controller: 'human', army: 0.9, techs: ['defense_in_depth'] },
      ai('veteran', { army: 1.5, techs: ['motorization'], extraUnits: [{ type: 'armor', count: 4 }] }),
    ],
  },
  /**
   * July 1944: inflatable tanks and fake radio traffic (the US 23rd Headquarters Special Troops, and
   * Operation Fortitude's phantom army) held the German 15th Army in the Pas-de-Calais. The
   * Americans are briefed; the British fight beside them.
   */
  'ghost-army': {
    id: 'ghost-army',
    inspiredBy: 'Operation Fortitude & the Ghost Army, 1944',
    theatre: 'normandy-1944',
    mapSize: THEATRES['normandy-1944'].mapSize,
    season: 'summer',
    dayLimit: 30,
    vpVictoryShare: 0.75,
    factions: [
      {
        controller: 'human',
        team: 0,
        army: 1.2,
        techs: ['deception'],
        extraUnits: [{ type: 'decoy', count: 5 }],
      },
      ai('veteran', { team: 0, army: 1.2 }),
      ai('veteran', { team: 1, techs: ['aerial_recon'] }),
    ],
  },
  /**
   * September 1939: the Polish Cipher Bureau had broken Enigma years before the war (Rejewski,
   * Różycki, Zygalski) — Poland, outnumbered and nearly surrounded, reads the Wehrmacht's mail.
   */
  enigma: {
    id: 'enigma',
    inspiredBy: 'The Polish Cipher Bureau and Enigma, September 1939',
    theatre: 'poland-1939',
    mapSize: THEATRES['poland-1939'].mapSize,
    season: 'summer',
    dayLimit: 30,
    vpVictoryShare: 0.6,
    startingTechs: ['field_telephone', 'radio'],
    factions: [
      { controller: 'human', techs: ['codebreaking', 'codebreaking2'] },
      ai('general', { techs: ['encryption'], army: 1.05 }),
    ],
  },
  /**
   * November 1942: Operation Uranus — the Soviet fronts broke through the Romanian armies on both
   * flanks of Stalingrad and closed the ring around the German 6th Army at Kalach.
   */
  'winter-kessel': {
    id: 'winter-kessel',
    inspiredBy: 'Operation Uranus, November 1942',
    theatre: 'stalingrad-1942',
    mapSize: THEATRES['stalingrad-1942'].mapSize,
    season: 'winter',
    dayLimit: 20,
    vpVictoryShare: 0.7,
    startingTechs: ['motorization', 'radio'],
    factions: [
      {
        controller: 'human',
        team: 0,
        army: 1.5,
        extraUnits: [
          { type: 'armor', count: 5 },
          { type: 'motorized', count: 2 },
        ],
      },
      ai('veteran', { team: 1 }),
      ai('recruit', { team: 1, army: 0.7 }),
      ai('recruit', { team: 1, army: 0.6 }),
    ],
  },
};

export const SCENARIO_IDS = Object.keys(SCENARIOS) as ScenarioId[];

/**
 * Build a playable config from a preset. `difficulty` overrides every AI slot's difficulty when
 * given (the player's chosen challenge), `seed` picks the theatre.
 */
export function scenarioConfig(id: ScenarioId, seed: number, difficulty?: AiDifficulty): SessionConfig {
  const def = SCENARIOS[id];
  return {
    seed,
    mapSize: def.mapSize,
    season: def.season,
    dayLimit: def.dayLimit,
    vpVictoryShare: def.vpVictoryShare,
    startingTechs: def.startingTechs ? [...def.startingTechs] : undefined,
    scenario: id,
    ...(def.theatre ? { theatre: def.theatre } : {}),
    factions: def.factions.map((f) => ({
      ...f,
      techs: f.techs ? [...f.techs] : undefined,
      extraUnits: f.extraUnits?.map((e) => ({ ...e })),
      aiDifficulty: f.controller === 'ai' ? (difficulty ?? f.aiDifficulty) : f.aiDifficulty,
    })),
  };
}
